import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import AssetRegisterReport from './AssetRegisterReport';
import CurrentAssignmentsReport from './CurrentAssignmentsReport';
import UpcomingOverdueMaintenanceReport from './UpcomingOverdueMaintenanceReport';
import MaintenanceRepairCostReport from './MaintenanceRepairCostReport';
import AssetStatusBreakdownReport from './AssetStatusBreakdownReport';
import VehicleRegistrationsReport from './VehicleRegistrationsReport';
import AssetInspectionsReport from './AssetInspectionsReport';

const REPORT_TABS = [
    { id: 'register', label: 'Asset Register' },
    { id: 'assignments', label: 'Current Assignments' },
    { id: 'overdue', label: 'Upcoming Overdue Maintenance' },
    { id: 'cost', label: 'Maintenance Repair Cost' },
    { id: 'status', label: 'Asset Status Breakdown' },
    { id: 'vehicle-registrations', label: 'Vehicle Registrations' },
    { id: 'inspections', label: 'Asset Inspections' }
];

const AssetReports: React.FC = () => {
    const [activeReport, setActiveReport] = useState('register');
    const [reportSearchQuery, setReportSearchQuery] = useState('');

    const activeLabel = REPORT_TABS.find(tab => tab.id === activeReport)?.label || 'Select a report';

    const filteredTabs = REPORT_TABS.filter(tab =>
        tab.label.toLowerCase().includes(reportSearchQuery.toLowerCase())
    );

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500">
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                <div>
                    <h4 className="font-bold text-gray-900 text-lg">Reports</h4>
                    <p className="text-gray-500 text-sm font-medium">Generate and view asset management reports</p>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="relative w-full max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search reports..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={reportSearchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="w-full lg:max-w-md">
                        <Select value={activeReport} onValueChange={setActiveReport}>
                            <SelectTrigger className="h-11 px-4 rounded-xl border-gray-100 bg-gray-50/50 text-sm font-bold text-gray-700 w-full">
                                <SelectValue placeholder="Select a report" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                                {filteredTabs.map((tab) => (
                                    <SelectItem
                                        key={tab.id}
                                        value={tab.id}
                                        className="font-bold"
                                    >
                                        {tab.label}
                                    </SelectItem>
                                ))}
                                {filteredTabs.length === 0 && (
                                    <SelectItem value="" disabled>
                                        No reports found
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Active Report Content */}
            <div className="w-full">
                {activeReport === 'register' && <AssetRegisterReport />}
                {activeReport === 'assignments' && <CurrentAssignmentsReport />}
                {activeReport === 'overdue' && <UpcomingOverdueMaintenanceReport />}
                {activeReport === 'cost' && <MaintenanceRepairCostReport />}
                {activeReport === 'status' && <AssetStatusBreakdownReport />}
                {activeReport === 'vehicle-registrations' && <VehicleRegistrationsReport />}
                {activeReport === 'inspections' && <AssetInspectionsReport />}
            </div>
        </div>
    );
};

export default AssetReports;
